/**
 * Persist placement from viewer `ifc:transforms` onto IfcModel#transform.
 * API permit-list is only x/y/z/rotX/rotY/rotZ (unknown keys are stripped).
 * `federationCleared` cannot be stored on the API — host localStorage keeps it.
 */

export const DEFAULT_IFC_TRANSFORM = {
  x: 0,
  y: 0,
  z: 0,
  rotX: 0,
  rotY: 0,
  rotZ: 0,
};

const axisValue = (transform, key) => {
  const raw = transform?.[key];
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function isLeftoverHeuristicUpright(transform) {
  const rotX = axisValue(transform, "rotX");
  const rotY = axisValue(transform, "rotY");
  return (rotX === 90 && rotY === 0) || (rotX === 0 && rotY === -90);
}

/**
 * Placement the API can store: rematch XYZ + yaw.
 * Leftover `rotX=90` / `rotY=-90` is stripped unless admin Rett opp
 * (`keepUprightTilt`) or a confirmed axis.
 */
export function placementTransform(transform, { keepUprightTilt = false } = {}) {
  const next = {
    x: axisValue(transform, "x"),
    y: axisValue(transform, "y"),
    z: axisValue(transform, "z"),
    rotX: axisValue(transform, "rotX"),
    rotY: axisValue(transform, "rotY"),
    rotZ: axisValue(transform, "rotZ"),
  };
  if (keepUprightTilt || !isLeftoverHeuristicUpright(next)) return next;
  return { ...next, rotX: 0, rotY: 0 };
}

/** PUT body for `updateIfcModel` — same keys the API already accepts. */
export function ifcModelTransformPayload(transform, { keepUprightTilt = false } = {}) {
  const payload = {
    transform: placementTransform(transform, { keepUprightTilt }),
  };
  // Only Rett opp (or an already-confirmed model) may set the flag.
  // Rematch must not confirm a stripped leftover 90.
  if (keepUprightTilt) payload.up_axis_confirmed = true;
  return payload;
}

/**
 * Rows the host should PUT after `ifc:transforms`.
 *
 * Force rematch must persist even when the result is native 0,0,0 — otherwise
 * a stale saved placement survives refresh and `onlyUnplaced` will not rematch.
 */
export function selectViewerTransformsToPersist(
  rows,
  {
    forceRematch = false,
    keptIds = new Set(),
    keepUprightTilt = false,
    confirmedIds = new Set(),
  } = {},
) {
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => {
      if (!row?.modelId || !row?.transform) return false;
      // Rett opp must persist even when replace copied XYZ onto keptIds.
      if (keepUprightTilt) return true;
      if (!forceRematch && keptIds.has(String(row.modelId))) return false;
      if (forceRematch) return true;
      const incoming = { ...DEFAULT_IFC_TRANSFORM, ...row.transform };
      const leftoverOnly =
        isLeftoverHeuristicUpright(incoming) &&
        axisValue(incoming, "x") === 0 &&
        axisValue(incoming, "y") === 0 &&
        axisValue(incoming, "z") === 0;
      return !leftoverOnly;
    })
    .map((row) => {
      const keepTilt =
        keepUprightTilt || confirmedIds.has(String(row.modelId));
      return {
        modelId: String(row.modelId),
        transform: {
          ...placementTransform(row.transform, { keepUprightTilt: keepTilt }),
          federationCleared:
            row.transform?.federationCleared === true ||
            forceRematch ||
            keepTilt,
        },
      };
    });
}

/** Viewer posts Promang IfcModel ids — refuse unknown session leftovers. */
export function resolveHostModelId(modelId, models) {
  if (modelId == null || modelId === "") return null;
  const hit = (models || []).find((model) => String(model.id) === String(modelId));
  return hit ? String(hit.id) : null;
}

export function resolvePersistRows(rows, models, { keepUprightTilt = false } = {}) {
  const resolved = [];
  const failedIds = [];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const modelId = resolveHostModelId(row?.modelId, models);
    if (!modelId || !row?.transform) {
      failedIds.push(String(row?.modelId || ""));
      return;
    }
    const model = (models || []).find((item) => String(item.id) === modelId);
    const keepTilt = keepUprightTilt || model?.up_axis_confirmed === true;
    resolved.push({
      modelId,
      transform: {
        ...placementTransform(row.transform, { keepUprightTilt: keepTilt }),
        federationCleared: row.transform?.federationCleared !== false,
      },
      uprightConfirmed: keepTilt,
    });
  });
  return { resolved, failedIds };
}

export function isRematchTransformsMessage(data) {
  return data?.reason === "remerge";
}

/**
 * Decide what the host persist handler should PUT after `ifc:transforms`.
 * Dialog Lagre writes baseline+nudge itself — rematch must not overwrite it.
 */
export function planIfcTransformsPersist(
  data,
  {
    ignoreDialog = false,
    pendingRemerge = false,
    keptIds = new Set(),
    confirmedIds = new Set(),
  } = {},
) {
  if (ignoreDialog) {
    return { persist: false, rows: [], forceRematch: false, reason: "dialog" };
  }
  if (!Array.isArray(data?.transforms)) {
    return { persist: false, rows: [], forceRematch: false, reason: "invalid" };
  }
  const keepUprightTilt = data?.reason === "upright";
  const forceRematch =
    Boolean(pendingRemerge) || isRematchTransformsMessage(data);
  const rows = selectViewerTransformsToPersist(data.transforms, {
    forceRematch,
    keptIds,
    keepUprightTilt,
    confirmedIds,
  });
  return { persist: true, rows, forceRematch, keepUprightTilt, reason: null };
}

const hasNonZeroXyz = (transform) =>
  axisValue(transform, "x") !== 0 ||
  axisValue(transform, "y") !== 0 ||
  axisValue(transform, "z") !== 0;

/**
 * Rematch computed XYZ with federation cleared. SessionBuilder sends the
 * same hash. Reload must apply this transform only — not MapConversion on top.
 * Identity rematch is locked via `federationCleared` (API cannot store it).
 */
export function mapFederationAllowedOnReload(transform) {
  if (transform?.federationCleared) return false;
  return !hasNonZeroXyz(transform);
}

/** Authored placement the next session must apply (same as the PUT body). */
export function reloadPlacementFromPersistPayload(payload) {
  return placementTransform(payload?.transform);
}

export function placementRecordFromPersist(row) {
  return {
    transform: placementTransform(row?.transform),
    federationCleared: true,
  };
}

/** Prefer the host lock (identity rematch) over API 0,0,0. */
export function mergeReloadPlacement(
  stored,
  apiTransform,
  { upAxisConfirmed = false } = {},
) {
  const keepUprightTilt =
    upAxisConfirmed || stored?.uprightConfirmed === true;
  const api = placementTransform(apiTransform, { keepUprightTilt });
  const confirmedOriginTilt =
    keepUprightTilt && isLeftoverHeuristicUpright(api);
  const staleRematchLock =
    stored?.federationCleared === true && stored?.uprightConfirmed !== true;
  // A rematch lock without uprightConfirmed must not hide a later Rett opp save.
  if (
    stored?.federationCleared &&
    stored.transform &&
    !(confirmedOriginTilt && staleRematchLock)
  ) {
    return {
      ...placementTransform(stored.transform, { keepUprightTilt }),
      federationCleared: true,
    };
  }
  if (hasNonZeroXyz(api) || confirmedOriginTilt) {
    return { ...api, federationCleared: true };
  }
  return api;
}

export function applyReloadPlacement(saved, mapDelta) {
  const map = mapFederationAllowedOnReload(saved);
  return {
    x: (saved?.x || 0) + (map ? mapDelta?.x || 0 : 0),
    y: (saved?.y || 0) + (map ? mapDelta?.y || 0 : 0),
    z: (saved?.z || 0) + (map ? mapDelta?.z || 0 : 0),
  };
}

/**
 * Session-open rematch (`ifc:transforms` without reason) must not overwrite
 * a locked rematch. Force rematch / place / dialog align still persist.
 */
export function filterUnlockedPersistRows(
  rows,
  lockedIds,
  { forceRematch = false, reason = null } = {},
) {
  if (
    forceRematch ||
    reason === "remerge" ||
    reason === "place" ||
    reason === "align" ||
    reason === "upright"
  ) {
    return rows;
  }
  return (Array.isArray(rows) ? rows : []).filter(
    (row) => !lockedIds.has(String(row.modelId)),
  );
}

export function collectSavedPlacements(models, storedById) {
  const out = [];
  (models || []).forEach((model) => {
    const modelId = String(model.id);
    const merged = mergeReloadPlacement(storedById?.[modelId], model.transform, {
      upAxisConfirmed: model.up_axis_confirmed === true,
    });
    if (!merged.federationCleared && !hasNonZeroXyz(merged)) return;
    out.push({
      modelId,
      transform: merged,
      upAxisConfirmed:
        model.up_axis_confirmed === true ||
        storedById?.[modelId]?.uprightConfirmed === true,
    });
  });
  return out;
}

export function persistPutsAllSucceeded(result, expectedCount) {
  return Boolean(
    result?.ok &&
      result.putCount === expectedCount &&
      expectedCount > 0 &&
      !(result.failedIds || []).length,
  );
}
